import React, { useReducer, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { Card, message } from 'antd';
import { Tabs } from 'antd';
import produce from 'immer';
import { TableMainText, TableSecondaryText } from 'styles';

import { useAudienceList } from 'api/audiences';
import { FEATURES, MID_PAGE_SIZE } from 'appConstants';
import { TableActions, TableTimestamp } from 'components';
import { TableWithNoWrapHeader } from 'components/StyledTable';
import { PageContent, PageHeader } from 'components/page';
import { usePermissions } from 'hooks';
import useFeatureFlags from 'hooks/useFeatureFlags';
import API, { downloadFile } from 'utils/request';

import AudienceSizeColumn from '../components/AudienceSizeColumn';

interface Props {}

const tabsList = [
  { key: 'all', label: 'All' }
  // hide the following two tabs until further notice
  // { key: 'active', label: 'Active' },
  // { key: 'inactive', label: 'Inactive' }
];

enum SortByEnum {
  ascend = 'ascend',
  descend = 'descend'
}

enum OrderEnum {
  asc = 'asc',
  desc = 'desc'
}

interface AudienceListPageState {
  page: number;
  size: number;
  sort_by?: string;
  search_string?: string;
  order?: OrderEnum;
  state: 'all';
}

const AudienceListPage = (props: Props) => {
  const [recalculatingAudiences, setRecalculatingAudiences] = useState<Set<number>>(new Set());
  const [erroredAudiences, setErroredAudiences] = useState<Set<number>>(new Set());

  const featureFlags = useFeatureFlags();
  const isAudienceSizeEnabled = featureFlags[FEATURES.AUDIENCE_COUNT_SIZE.key];

  const [params, paramsDispatch] = useReducer(
    (state: AudienceListPageState, action): AudienceListPageState =>
      produce(state, draft => {
        switch (action.type) {
          case 'SEARCH':
            draft.search_string = action.value;
            break;
          case 'TABLE': {
            draft.page = action.pagination;
            draft.sort_by = action.sorter.columnKey;
            draft.order =
              action.sorter.order === SortByEnum.ascend ? OrderEnum.asc : OrderEnum.desc;

            return draft;
          }
          case 'STATE':
            draft.state = action.state === 'all' ? null : action.state;
            draft.page = 1;
        }
      }),
    {
      page: 1,
      size: MID_PAGE_SIZE,
      state: null,
      search_string: null,
      sort_by: 'created_at',
      order: OrderEnum.desc
    }
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'title',
      key: 'name',
      sorter: false,
      render: (_, record) => (
        <Link to={`/p/audiences/show/${record.id}`} style={{ display: 'inline-block' }}>
          <TableMainText>{record.name}</TableMainText>
          <TableSecondaryText>ID: {record.id}</TableSecondaryText>
        </Link>
      )
    },
    ...(isAudienceSizeEnabled
      ? [
          {
            title: 'Audience Size',
            dataIndex: 'audience_size',
            key: 'audience_size',
            render: (_, record) => (
              <AudienceSizeColumn
                record={record}
                recalculatingAudiences={recalculatingAudiences}
                erroredAudiences={erroredAudiences}
                onRecalculateStart={handleRecalculateStart}
                onRecalculateEnd={handleRecalculateEnd}
              />
            )
          }
        ]
      : []),
    {
      title: 'Created At',
      key: 'created_at',
      dataIndex: 'created_at',
      sorter: true,
      render: (_, record) => <TableTimestamp timestamp={record.created_at} />
    },
    {
      title: 'Updated At',
      key: 'updated_at',
      dataIndex: 'updated_at',
      sorter: true,
      render: (_, record) => <TableTimestamp timestamp={record.updated_at} />
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => getActionsList(record),
      align: 'right' as 'right'
    }
  ];

  const { hasDraftPermission, canEdit } = usePermissions('audiences');

  const navigate = useNavigate();

  const { data: audiences, meta, isLoading } = useAudienceList(params);

  useEffect(() => {
    if (audiences && !isLoading) {
      const audiencesWithFreshData = audiences.filter(
        audience =>
          recalculatingAudiences.has(audience.id) &&
          audience.user_account_counts != null &&
          audience.last_counted_at != null
      );

      if (audiencesWithFreshData.length > 0) {
        const audienceIdsToRemove = audiencesWithFreshData.map(audience => audience.id);

        setRecalculatingAudiences(prev => {
          const newSet = new Set(prev);
          audienceIdsToRemove.forEach(id => newSet.delete(id));
          return newSet;
        });

        setErroredAudiences(prev => {
          const newSet = new Set(prev);
          audienceIdsToRemove.forEach(id => newSet.delete(id));
          return newSet;
        });
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [params.page, isLoading, audiences]);

  const handleRecalculateStart = (audienceId: number) => {
    setRecalculatingAudiences(prev => new Set(prev).add(audienceId));
    setErroredAudiences(prev => {
      const newSet = new Set(prev);
      newSet.delete(audienceId);
      return newSet;
    });
  };

  const handleRecalculateEnd = (audienceId: number, isErrored = false) => {
    if (isErrored) {
      setRecalculatingAudiences(prev => {
        const newSet = new Set(prev);
        newSet.delete(audienceId);
        return newSet;
      });
      setErroredAudiences(prev => new Set(prev).add(audienceId));
    }
  };

  const onExport = record => {
    API.post(`/v4/dash/v4_audiences/${record.id}/export`)
      .then(() => {
        message.info('The exported file will be sent to your email.');
      })
      .catch(() => {
        message.error('Error exporting audience.');
      });
  };

  const onDownload = async record => {
    API.get(`/v4/dash/v4_audiences/${record.id}/download`, {
      responseType: 'blob'
    })
      .then((res: any) => {
        downloadFile(res.data, `${record.id}.csv`);
      })
      .catch(() => {
        message.error('Error downloading audience.');
      });
  };

  const getActionsList = record => {
    const actions = [];

    // edit is vislbe if user has edit permission OR user has draft permission and record is in draft state
    if (canEdit || (hasDraftPermission && record.state === 'draft')) {
      actions.push({
        key: 'edit',
        title: 'Edit',
        onClick: e => {
          e.stopPropagation();
          navigate(`/p/audiences/edit/${record.id}`);
        }
      });
      actions.push({
        key: 'export',
        title: 'Export',
        onClick: () => onExport(record)
      });
      actions.push({
        key: 'download',
        title: 'Download',
        onClick: () => onDownload(record)
      });
    }
    // only return table actions element if there are any actions to list
    return actions.length ? <TableActions actions={actions} /> : null;
  };

  const onTableChange = (pagination, filters, sorter) => {
    paramsDispatch({ type: 'TABLE', pagination: pagination.current, sorter: sorter });
  };

  return (
    <>
      <PageHeader
        title="Audiences"
        showPrimaryAction={hasDraftPermission || canEdit}
        primaryAction={{
          icon: <PlusOutlined />,
          onClick: () => navigate('/p/audiences/create'),
          label: 'Create New Audience'
        }}
      />
      <PageContent>
        <Card>
          {/* Hide until https://perxtechnologies.atlassian.net/browse/VS-7058
          will be completed
          <SearchBar
            onSearch={value => paramsDispatch({ type: 'SEARCH', value })}
            placeholder="Search rule by name or ID"
          /> */}
          <Tabs
            activeKey={!params.state ? 'all' : params.state}
            onChange={value => paramsDispatch({ type: 'STATE', state: value })}
            tabBarStyle={{ marginBottom: 0 }}
            items={tabsList.map(t => ({ label: t.label, key: t.key }))}
          />
          <TableWithNoWrapHeader<any>
            rowKey="id"
            columns={columns}
            dataSource={audiences || []}
            loading={isLoading}
            onChange={onTableChange}
            pagination={{
              position: ['bottomRight'],
              current: params.page,
              defaultPageSize: MID_PAGE_SIZE,
              pageSize: params.size,
              total: meta?.total_count || 0
            }}
          />
        </Card>
      </PageContent>
    </>
  );
};

export default AudienceListPage;
